import { useMemo, useState, type CSSProperties } from "react";
import { Check } from "lucide-react";
import { cn } from "@/utils/helpers";
import {
  MEDIA_TYPE_LABEL,
  SLOT_ASPECT_RATIOS_BY_MEDIA_TYPE,
  VIDEO_LENGTH_LABEL,
  orientationForAspectRatio,
  type AspectRatio,
  type CalendarMediaType,
  type CalendarVideoLength,
} from "@/types/contentCalendar.type";
import type { NewPost } from "./MonthGrid";
import { buildPillarHueMap } from "../../strategy/components/strategyMeta";
import { kitButtonClass } from "../../components/kit/Button";
import { kitFieldSurfaceClass } from "../../components/kit/Field";
import { KitSegmentedControl } from "../../components/kit/SegmentedControl";
import { KitStepper } from "../../components/kit/Switch";
import { MAX_SLIDES } from "@/types/scheduling.type";
import "./calendar.css";

/**
 * ADDING A POST.
 *
 * It was a text field and three buttons crammed into a 96px day cell, which is
 * the width of about nine characters. You could give a post a title and a
 * format and nothing else: no pillar, no way to see the day you had picked,
 * and no room to read either back before committing.
 *
 * So it moved out of the cell and asks for what the endpoint actually takes:
 * `POST /plans/:planId/slots` is `{title (1-160), mediaType, aspectRatio,
 * videoLength?, pillarId?, scheduledAt}`. Day and title are required; the
 * format has a sensible default; the pillar is optional and stays optional,
 * because the planner assigns pillars and a person adding one post by hand
 * should not have to.
 *
 * ONE FORM FOR BOTH SHAPES. The grid opens it with a day already chosen (that
 * is what the `+` on a day is for), and the list opens it with today, or the
 * first day of the month if the month is not the current one. Either way the
 * day is a field you can see and change, rather than a decision the gesture
 * made silently for you.
 *
 * Media and canvas are separate decisions. This is what makes VIDEO · 4:5 a
 * normal production spec instead of forcing people through orientation jargon.
 */

const TITLE_MAX = 160;

export interface PillarChoice {
  id: string;
  name: string;
  order?: number;
}

export default function AddPostForm({
  year,
  month,
  day,
  pillars,
  onAdd,
  onClose,
  bare = false,
  phone = false,
  submitLabel = "Add post",
  titlePlaceholder = "A working title. The ideas come later",
  initialTitle = "",
  slides = false,
}: {
  /** A quick post: a static one can have several slides (a carousel). */
  slides?: boolean;
  /** The line the person typed in the chat card, as the working title. */
  initialTitle?: string;
  /** A quick post has no ideas to come, so the chat says less. */
  titlePlaceholder?: string;
  /** The submit button's words ("Add post", or "Add quick post" from the chat). */
  submitLabel?: string;
  /** On a phone the fields read at 16px and every control is a 44px target. */
  phone?: boolean;
  year: number;
  /** 1-12. */
  month: number;
  /** Pre-filled from the day whose `+` was pressed. */
  day: number;
  /** The active strategy's promoted pillars. Empty when there is no strategy. */
  pillars?: PillarChoice[];
  onAdd: (post: NewPost) => Promise<void> | void;
  onClose: () => void;
  /** Inside a dialog: no card of its own. */
  bare?: boolean;
}) {
  const [title, setTitle] = useState(initialTitle.slice(0, TITLE_MAX));
  const [mediaType, setMediaType] = useState<CalendarMediaType>("VIDEO");
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>("9:16");
  const [videoLength, setVideoLength] = useState<CalendarVideoLength>("SHORT_FORM");
  const [pillar, setPillar] = useState<PillarChoice | null>(null);
  const [chosenDay, setChosenDay] = useState(day);
  const [slideCount, setSlideCount] = useState(1);
  const [busy, setBusy] = useState(false);
  const pillarHues = useMemo(() => buildPillarHueMap(pillars ?? []), [pillars]);

  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const clean = title.trim();

  // Act on the confirm: the add is optimistic in the cache and its failure
  // toasts and rolls back, so the form closes at once.
  const commit = () => {
    if (!clean || busy) return;
    setBusy(true);
    void Promise.resolve(
      onAdd({
        day: chosenDay,
        title: clean,
        mediaType,
        aspectRatio,
        orientation: orientationForAspectRatio(aspectRatio),
        videoLength: mediaType === "VIDEO" ? videoLength : null,
        pillarId: pillar?.id ?? null,
        pillarName: pillar?.name ?? "",
        ...(slides && mediaType === "STATIC" ? { slideCount } : {}),
      }),
    ).catch(() => undefined);
    onClose();
  };

  const label = cn("text-co-muted", phone ? "text-[14px]" : "text-[12.5px]");
  const segSize = phone ? "large" : "default";

  return (
    <section
      aria-label="Add a post"
      className={cn("co-add-post flex flex-col gap-4", !bare && "rounded-card bg-co-surface p-4")}
      onKeyDown={(e) => {
        if (e.key === "Escape" && !busy) onClose();
      }}
    >
      <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
        <label className="flex min-w-[16rem] flex-1 flex-col gap-1.5">
          <span className={label}>What is it?</span>
          <span className={cn("co-text-field flex items-center px-3.5", kitFieldSurfaceClass, phone ? "h-11" : "h-10")}>
            <input
              autoFocus
              value={title}
              maxLength={TITLE_MAX}
              disabled={busy}
              placeholder={titlePlaceholder}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") commit();
              }}
              className={cn(
                "co-input-own-focus caret-co-accent h-full min-w-0 flex-1 bg-transparent text-co-text outline-none placeholder:text-co-faint focus-visible:!shadow-none",
                phone ? "text-[16px]" : "text-[14px]",
              )}
            />
          </span>
        </label>

        <span className="flex flex-col gap-1.5">
          <span className={label}>Day</span>
          <KitStepper
            label="Day"
            value={chosenDay}
            min={1}
            max={lastDay}
            large={phone}
            disabled={busy}
            onChange={setChosenDay}
          />
        </span>
      </div>

      <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
        <span className="flex flex-col gap-1.5">
          <span className={label}>Media</span>
          <KitSegmentedControl<CalendarMediaType>
            variant="lens"
            size={segSize}
            ariaLabel="Media"
            options={(["VIDEO", "STATIC"] as const).map((type) => ({ label: MEDIA_TYPE_LABEL[type], value: type }))}
            value={mediaType}
            onChange={(type) => {
              setMediaType(type);
              setAspectRatio(type === "STATIC" ? "4:5" : "9:16");
            }}
          />
        </span>

        <span className="flex flex-col gap-1.5">
          <span className={label}>Ratio</span>
          <KitSegmentedControl<AspectRatio>
            variant="lens"
            size={segSize}
            ariaLabel="Ratio"
            options={SLOT_ASPECT_RATIOS_BY_MEDIA_TYPE[mediaType].map((ratio) => ({ label: ratio, value: ratio }))}
            value={aspectRatio}
            onChange={setAspectRatio}
          />
        </span>

        {slides && mediaType === "STATIC" && (
          <span className="flex flex-col gap-1.5">
            <span className={label}>Slides</span>
            <KitStepper
              label="Slides"
              value={slideCount}
              min={1}
              max={MAX_SLIDES}
              large={phone}
              disabled={busy}
              onChange={setSlideCount}
            />
          </span>
        )}

        {mediaType === "VIDEO" && (
          <span className="flex flex-col gap-1.5">
            <span className={label}>Length</span>
            <KitSegmentedControl<CalendarVideoLength>
              variant="lens"
              size={segSize}
              ariaLabel="Length"
              options={(["SHORT_FORM", "LONG_FORM"] as const).map((length) => ({ label: VIDEO_LENGTH_LABEL[length], value: length }))}
              value={videoLength}
              onChange={setVideoLength}
            />
          </span>
        )}

        {/* Optional, and it says so. A pillar is normally the planner's call
            (it chooses from the active strategy's promoted set), so a hand-added
            post without one is a perfectly ordinary post, not an incomplete
            one. Absent entirely when the brand has no active strategy, because
            then there is nothing to choose from. */}
        {pillars && pillars.length > 0 && (
          <span className="flex flex-col gap-1.5">
            <span className={label}>Pillar, if you know it</span>
            <span className="flex flex-wrap items-center gap-1.5">
              {pillars.map((p) => {
                const selected = pillar?.id === p.id;
                const hue = pillarHues.get(p.id);
                return (
                  <button
                    key={p.id}
                    type="button"
                    aria-pressed={selected}
                    data-selected={selected}
                    disabled={busy}
                    onClick={() => setPillar((cur) => (cur?.id === p.id ? null : p))}
                    className={cn(
                      "co-pillar-identity co-pillar-pill inline-flex items-center gap-1.5 rounded-pill border px-3 transition disabled:opacity-50",
                      phone ? "h-11 px-3.5 text-[16px]" : "h-8 text-[13px]",
                      !hue && "border-co-line2 text-co-muted",
                    )}
                    style={hue ? ({ "--co-pillar-hue": hue } as CSSProperties) : undefined}
                  >
                    {selected ? (
                      <Check aria-hidden className="size-3" />
                    ) : (
                      <span aria-hidden className="co-pillar-mark size-1.5 rounded-full" />
                    )}
                    {p.name}
                  </button>
                );
              })}
            </span>
          </span>
        )}

        <span className="ml-auto flex items-center gap-3">
          <button
            type="button"
            disabled={busy}
            onClick={onClose}
            className={cn(kitButtonClass({ variant: "ghost", size: phone ? "lg" : "sm" }))}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!clean || busy}
            onClick={commit}
            className={cn(kitButtonClass({ variant: "primary", size: phone ? "lg" : "sm" }))}
          >
            {submitLabel}
          </button>
        </span>
      </div>
    </section>
  );
}
